import React, { useEffect, useRef, useState } from 'react';
import { Note } from '../../types';
import { createNote, deleteNote, fetchNotes } from '../../services/api';

interface Props {
  subjectType: 'leads' | 'deals';
  subjectId: number;
}

const NotesList: React.FC<Props> = ({ subjectType, subjectId }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchNotes(subjectType, subjectId).then(setNotes);
  }, [subjectType, subjectId]);

  const handleAdd = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const note = await createNote(subjectType, subjectId, content.trim());
      setNotes([note, ...notes]);
      setContent('');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    await deleteNote(subjectType, subjectId, noteId);
    setNotes(notes.filter((n) => n.id !== noteId));
  };

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  return (
    <div className="notes-list">
      <div className="add-note-form">
        <textarea
          ref={textareaRef}
          className="add-note-textarea"
          placeholder="Add a note…"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
        />
        <button
          className="btn btn-primary"
          onClick={handleAdd}
          disabled={saving || !content.trim()}
        >
          {saving ? 'Saving…' : 'Add Note'}
        </button>
      </div>

      {notes.length === 0 && (
        <p className="detail-info-empty">No notes yet.</p>
      )}

      {notes.map((note) => (
        <div className="note-item" key={note.id}>
          <div className="note-avatar">
            {note.user?.name?.charAt(0).toUpperCase() ?? 'U'}
          </div>
          <div className="note-body">
            <div className="note-header">
              <span className="note-author">{note.user?.name ?? 'Unknown'}</span>
              <span className="note-time">{formatTime(note.created_at)}</span>
              <button
                className="note-delete"
                onClick={() => handleDelete(note.id)}
                title="Delete note"
              >
                ×
              </button>
            </div>
            <div className="note-content">{note.content}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotesList;
